import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useLanguage } from '../context/LanguageContext';

const QuoteForm = ({ eventType, buttonColor, isHeaderModal = false, onClose }) => {
  const [open, setOpen] = useState(false);
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    fecha: null,
    invitados: '',
    mensaje: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({
      ...prev,
      fecha: date
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const dateLocale = language === 'es' ? es : enUS;
    
    // Validación básica
    if (!formData.nombre || !formData.telefono || !formData.fecha) {
      alert(t('fillRequiredFields'));
      return;
    }

    // Formatear mensaje para WhatsApp
    const mensaje = `
🎉 *${t('quoteRequest')} - ${eventType}*

👤 *${t('name')}:* ${formData.nombre}
📧 *${t('emailAddress')}:* ${formData.email || t('notProvided')}
📱 *${t('phoneNumber')}:* ${formData.telefono}
📅 *${t('eventDateLabel')}:* ${format(formData.fecha, 'dd/MM/yyyy', { locale: dateLocale })}
👥 *${t('numberOfGuests')}:* ${formData.invitados || t('notSpecified')}

💬 *${t('additionalMessageLabel')}:*
${formData.mensaje || t('none')}

---
_${t('generatedFrom')}_
    `.trim();

    // Codificar mensaje para URL
    const mensajeCodificado = encodeURIComponent(mensaje);
    
    // Número de WhatsApp (formato internacional sin +)
    const numeroWhatsApp = '16145974338';
    
    // Abrir WhatsApp
    const urlWhatsApp = `https://wa.me/${numeroWhatsApp}?text=${mensajeCodificado}`;
    window.open(urlWhatsApp, '_blank');
    
    // Cerrar modal y resetear formulario
    setOpen(false);
    if (onClose) onClose();
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      fecha: null,
      invitados: '',
      mensaje: ''
    });
  };

  // Si es modal del header, renderizar solo el formulario
  if (isHeaderModal) {
    return (
      <div className="quote-form-standalone">
        <h2 className="quote-modal-title">
          {t('quoteModalTitle')} - {eventType}
        </h2>
        <form onSubmit={handleSubmit} className="quote-form">
          <div className="form-group">
            <Label htmlFor="nombre-header" className="form-label">
              {t('fullName')} <span className="required">*</span>
            </Label>
            <Input
              id="nombre-header"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder={t('fullNamePlaceholder')}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <Label htmlFor="email-header" className="form-label">
              {t('emailAddress')}
            </Label>
            <Input
              id="email-header"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('emailPlaceholder')}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="telefono-header" className="form-label">
              {t('phoneNumber')} <span className="required">*</span>
            </Label>
            <Input
              id="telefono-header"
              name="telefono"
              type="tel"
              value={formData.telefono}
              onChange={handleChange}
              placeholder={t('phonePlaceholder')}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <Label className="form-label">
              {t('eventDate')} <span className="required">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="date-picker-button"
                >
                  <CalendarIcon className="calendar-icon" />
                  {formData.fecha ? (
                    format(formData.fecha, 'dd/MM/yyyy', { locale: language === 'es' ? es : enUS })
                  ) : (
                    <span>{t('selectDate')}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="calendar-popover">
                <Calendar
                  mode="single"
                  selected={formData.fecha}
                  onSelect={handleDateSelect}
                  locale={language === 'es' ? es : enUS}
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="form-group">
            <Label htmlFor="invitados-header" className="form-label">
              {t('approximateGuests')}
            </Label>
            <Input
              id="invitados-header"
              name="invitados"
              type="number"
              value={formData.invitados}
              onChange={handleChange}
              placeholder={t('guestsPlaceholder')}
              className="form-input"
              min="1"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="mensaje-header" className="form-label">
              {t('additionalMessage')}
            </Label>
            <Textarea
              id="mensaje-header"
              name="mensaje"
              value={formData.mensaje}
              onChange={handleChange}
              placeholder={t('messagePlaceholder')}
              className="form-textarea"
              rows={4}
            />
          </div>

          <div className="form-actions">
            <Button
              type="submit"
              className="submit-button"
              style={{ 
                backgroundColor: buttonColor,
                borderColor: buttonColor
              }}
            >
              {t('sendWhatsApp')}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button 
          className="cta-button-center"
          style={{ 
            backgroundColor: buttonColor,
            borderColor: buttonColor
          }}
        >
          {t('requestQuote')}
        </button>
      </DialogTrigger>
      <DialogContent className="quote-modal">
        <DialogHeader>
          <DialogTitle className="quote-modal-title">
            {t('quoteModalTitle')} - {eventType}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="quote-form">
          <div className="form-group">
            <Label htmlFor="nombre" className="form-label">
              {t('fullName')} <span className="required">*</span>
            </Label>
            <Input
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder={t('fullNamePlaceholder')}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <Label htmlFor="email" className="form-label">
              {t('emailAddress')}
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('emailPlaceholder')}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="telefono" className="form-label">
              {t('phoneNumber')} <span className="required">*</span>
            </Label>
            <Input
              id="telefono"
              name="telefono"
              type="tel"
              value={formData.telefono}
              onChange={handleChange}
              placeholder={t('phonePlaceholder')}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <Label className="form-label">
              {t('eventDate')} <span className="required">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="date-picker-button"
                >
                  <CalendarIcon className="calendar-icon" />
                  {formData.fecha ? (
                    format(formData.fecha, 'dd/MM/yyyy', { locale: language === 'es' ? es : enUS })
                  ) : (
                    <span>{t('selectDate')}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="calendar-popover">
                <Calendar
                  mode="single"
                  selected={formData.fecha}
                  onSelect={handleDateSelect}
                  locale={language === 'es' ? es : enUS}
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="form-group">
            <Label htmlFor="invitados" className="form-label">
              {t('approximateGuests')}
            </Label>
            <Input
              id="invitados"
              name="invitados"
              type="number"
              value={formData.invitados}
              onChange={handleChange}
              placeholder={t('guestsPlaceholder')}
              className="form-input"
              min="1"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="mensaje" className="form-label">
              {t('additionalMessage')}
            </Label>
            <Textarea
              id="mensaje"
              name="mensaje"
              value={formData.mensaje}
              onChange={handleChange}
              placeholder={t('messagePlaceholder')}
              className="form-textarea"
              rows={4}
            />
          </div>

          <div className="form-actions">
            <Button
              type="submit"
              className="submit-button"
              style={{ 
                backgroundColor: buttonColor,
                borderColor: buttonColor
              }}
            >
              {t('sendWhatsApp')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteForm;
